/**
 * ProfileGuard — com conta Swipe, o INTERIOR do app exige um perfil ativo.
 *
 * ── O buraco (10/10/2026) ──
 * As rotas internas passavam pelo `SwipeGuard` (quem é o usuário) e pelo `AuthGuard`
 * (a fonte vale), e por NADA que exigisse perfil. O `RootRoute` manda pra `/profiles`
 * quem chega por `/` sem perfil, mas qualquer outro caminho até o interior entrava com
 * `activeProfileId = null`: o voltar do navegador no web (o histórico de antes do
 * pareamento), um deep link, uma URL restaurada. Sem perfil o escopo é `pf:_main`, que
 * NÃO sincroniza (sync/sync-scope.ts): nada do que se assiste sobe, e o histórico da
 * conta não desce. No PC do dono (pareado por QR em 09/10) o servidor não registrou
 * perfil nem item.
 *
 * ── O invariante ──
 * Com conta Swipe, todo aparelho escolhe (ou cria) o perfil na tela de perfis antes de
 * entrar, e o perfil ativo é sempre um perfil REAL (sincronizável). Sem conta Swipe o
 * `SwipeGuard` já barra antes daqui; a decisão é pura (`profileGateRedirect`) pra ser
 * provada sem DOM.
 *
 * Decide no RENDER (como o `SwipeGuard`): devolver `<Navigate>` significa que a tela
 * sem perfil NUNCA monta, nem por um frame (nenhum save em `_main` escapa).
 */

import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'

import { useSessionStore } from '../store'
import { hasSwipeSession } from '../sync'

/** Pra onde mandar quem chega ao interior (`null` = pode entrar). */
export function profileGateRedirect(input: {
  swipe: boolean
  activeProfileId: string | null
}): '/profiles' | null {
  if (!input.swipe) return null
  if (!input.activeProfileId || input.activeProfileId === '_main') return '/profiles'
  return null
}

export function ProfileGuard({ children }: { children: ReactNode }) {
  const activeProfileId = useSessionStore((s) => s.activeProfileId)
  const target = profileGateRedirect({ swipe: hasSwipeSession(), activeProfileId })
  if (target) return <Navigate to={target} replace />
  return <>{children}</>
}
